import { SourcingChannelStatus } from 'api-clients/monolith';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { RemoveCloudIcon } from '@/components/icons/remove-cloud';
import { Tag } from '@/components/ui/tag';
import { cn } from '@/utils/classnames';

import { CampaignStatusGuideDialog } from './campaign-status-guide-dialog';

export const CAMPAIGN_STATUS_VALUES: SourcingChannelStatus[] = [
  'in_progress',
  'scheduled',
  'active',
  'stopped',
  'completed',
  'failed',
];

// Plain-string equivalents of STATUS_CONFIG's labels, for contexts (like a
// filter `Select`) that need a string rather than a `FormattedMessage` node.
export function useCampaignStatusLabels(): Record<
  SourcingChannelStatus,
  string
> {
  const intl = useIntl();

  return useMemo(
    () => ({
      in_progress: intl.formatMessage({
        defaultMessage: 'Publishing',
        description: 'Campaign status: job submitted to board but not yet live',
      }),
      scheduled: intl.formatMessage({
        defaultMessage: 'Scheduled',
        description:
          'Campaign status: campaign is set up and will go live on a future start date',
      }),
      active: intl.formatMessage({
        defaultMessage: 'Active',
        description: 'Campaign status: job is live and receiving applicants',
      }),
      stopped: intl.formatMessage({
        defaultMessage: 'Paused',
        description:
          'Campaign status: manually paused, job not visible on board',
      }),
      completed: intl.formatMessage({
        defaultMessage: 'Completed',
        description:
          'Campaign status: campaign has ended, budget spent or end date passed',
      }),
      failed: intl.formatMessage({
        defaultMessage: 'Failed',
        description: 'Campaign status: campaign has failed to launch',
      }),
    }),
    [intl],
  );
}

interface StatusConfig {
  bg: string;
  color: string;
  label: React.ReactNode;
}

const STATUS_CONFIG: Record<SourcingChannelStatus, StatusConfig> = {
  in_progress: {
    bg: 'bg-fx-brand-50',
    color: 'text-fx-brand',
    label: (
      <FormattedMessage
        defaultMessage="Publishing"
        description="Campaign status: job submitted to board but not yet live"
      />
    ),
  },
  scheduled: {
    bg: 'bg-fx-blue-50',
    color: 'text-fx-blue',
    label: (
      <FormattedMessage
        defaultMessage="Scheduled"
        description="Campaign status: campaign is set up and will go live on a future start date"
      />
    ),
  },
  active: {
    bg: 'bg-fx-green-50',
    color: 'text-fx-green',
    label: (
      <FormattedMessage
        defaultMessage="Active"
        description="Campaign status: job is live and receiving applicants"
      />
    ),
  },
  stopped: {
    bg: 'bg-amber-50',
    color: 'text-amber-600',
    label: (
      <FormattedMessage
        defaultMessage="Paused"
        description="Campaign status: manually paused, job not visible on board"
      />
    ),
  },
  completed: {
    bg: 'bg-fx-neutral-100',
    color: 'text-fx-neutral-600',
    label: (
      <FormattedMessage
        defaultMessage="Completed"
        description="Campaign status: campaign has ended, budget spent or end date passed"
      />
    ),
  },
  failed: {
    bg: 'bg-fx-rose-50',
    color: 'text-fx-rose-800',
    label: (
      <FormattedMessage
        defaultMessage="Failed"
        description="Campaign status: campaign has failed to launch"
      />
    ),
  },
};

interface ChannelStatusTagProps {
  status: SourcingChannelStatus;
  isNonIntegrated?: boolean;
}

export function ChannelStatusTag({
  status,
  isNonIntegrated,
}: ChannelStatusTagProps) {
  const config = STATUS_CONFIG[status];
  if (!config) return null;

  const icon = isNonIntegrated ? (
    <RemoveCloudIcon className={cn('shrink-0', config.color)} />
  ) : (
    <span
      className={cn(
        'h-1.5 w-1.5 shrink-0 rounded-full bg-current',
        config.color,
      )}
    />
  );

  return <Tag className={config.bg} icon={icon} text={config.label} />;
}

interface ChannelStatusPillProps {
  status: SourcingChannelStatus;
  isNonIntegrated?: boolean;
}

export function ChannelStatusPill({
  status,
  isNonIntegrated,
}: ChannelStatusPillProps) {
  const config = STATUS_CONFIG[status];
  if (!config) return null;

  return (
    <CampaignStatusGuideDialog>
      <button type="button" className="cursor-pointer">
        <ChannelStatusTag status={status} isNonIntegrated={isNonIntegrated} />
      </button>
    </CampaignStatusGuideDialog>
  );
}
